import {
    createContext,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import { TOOLTIP_OPEN_DELAY_MS, Tooltip } from "@design-system/Tooltip/Tooltip";
import { isOverflowingX } from "@design-system/Tooltip/useTruncationTooltip";

/**
 * Whether a `CellTooltipHost` serves this table's cells. The host listens on
 * the table root's DOM only, so content portaled out of it (a popup) renders
 * under `<CellTooltipHostContext.Provider value={false}>` to get `CellText`'s
 * own tooltip.
 */
export const CellTooltipHostContext = createContext(false);

const TARGET_SELECTOR = "[data-cell-tooltip]";

/** Like the tooltip provider's group: hovering on soon after a close skips the delay. */
const SKIP_DELAY_MS = 400;

interface CellTooltipTarget {
    element: HTMLElement;
    text: string;
}

const ALWAYS_SELECTOR = `${TARGET_SELECTOR}[data-cell-tooltip-always]`;

/**
 * Looking up from the cell itself skips enabled buttons: a button's tooltip
 * (a toggle's "Mark Done") names the button, not the cell.
 */
const NOT_ENABLED_BUTTON = ":not(button:enabled)";

/**
 * The tooltip text under the pointer. An always-shown reason wins over the
 * cut-off text inside it, and covers its whole cell, padding included.
 */
const findTarget = (node: EventTarget | null): HTMLElement | null => {
    if (!(node instanceof Element)) {
        return null;
    }
    const target =
        node.closest(ALWAYS_SELECTOR) ??
        node.closest(TARGET_SELECTOR) ??
        (node.closest("button:enabled") === null
            ? node
                  .closest('[role="gridcell"]')
                  ?.querySelector(`${ALWAYS_SELECTOR}${NOT_ENABLED_BUTTON}`)
            : null);
    return target instanceof HTMLElement ? target : null;
};

/** The truncating text of a keyboard-focused cell, not of a focused row. */
const findFocusTarget = (node: EventTarget | null): HTMLElement | null => {
    if (!(node instanceof HTMLElement) || !node.matches(":focus-visible")) {
        return null;
    }
    if (node.getAttribute("role") !== "gridcell") {
        return findTarget(node);
    }
    const target = node.querySelector(
        `${TARGET_SELECTOR}${NOT_ENABLED_BUTTON}`,
    );
    return target instanceof HTMLElement ? target : null;
};

/**
 * Listens on the table root for hover and keyboard focus on cell text, and
 * shows its full text in a tooltip while the text is cut off.
 */
const listenForTargets = ({
    root,
    onTargetChange,
}: {
    root: HTMLElement;
    onTargetChange: (target: CellTooltipTarget | null) => void;
}) => {
    let current: HTMLElement | null = null;
    let isOpen = false;
    let closedAt = Number.NEGATIVE_INFINITY;
    let openTimer: ReturnType<typeof setTimeout> | undefined;
    let textObserver: MutationObserver | undefined;

    const isShown = (element: HTMLElement) =>
        element.dataset.cellTooltipAlways !== undefined ||
        isOverflowingX(element);

    const show = (element: HTMLElement) => {
        if (!element.isConnected || !isShown(element)) {
            return;
        }
        isOpen = true;
        onTargetChange({ element, text: element.dataset.cellTooltip ?? "" });
        // A row re-render keeps the element and rewrites its text in place.
        textObserver = new MutationObserver(() => {
            const text = element.dataset.cellTooltip;
            if (text === undefined || !isShown(element)) {
                hide();
            } else {
                onTargetChange({ element, text });
            }
        });
        textObserver.observe(element, {
            attributeFilter: ["data-cell-tooltip"],
        });
    };
    const hide = () => {
        clearTimeout(openTimer);
        textObserver?.disconnect();
        textObserver = undefined;
        current = null;
        if (isOpen) {
            isOpen = false;
            closedAt = performance.now();
            onTargetChange(null);
        }
    };
    const open = (element: HTMLElement, delayMs: number) => {
        hide();
        current = element;
        if (delayMs === 0) {
            show(element);
        } else {
            openTimer = setTimeout(() => show(element), delayMs);
        }
    };
    const requestHover = (element: HTMLElement | null) => {
        if (element === null) {
            hide();
            return;
        }
        if (element === current) {
            return;
        }
        const isWarm = isOpen || performance.now() - closedAt < SKIP_DELAY_MS;
        open(element, isWarm ? 0 : TOOLTIP_OPEN_DELAY_MS);
    };
    // Arrow keys step through cells faster than the delay, so a warm tooltip
    // would re-anchor (and re-measure the layout) on every key.
    const requestFocus = (element: HTMLElement | null) => {
        if (element === null) {
            hide();
            return;
        }
        if (element !== current) {
            open(element, TOOLTIP_OPEN_DELAY_MS);
        }
    };

    const handlePointerOver = (event: PointerEvent) =>
        requestHover(
            root.hasAttribute("data-dt-resizing")
                ? null
                : findTarget(event.target),
        );
    const handlePointerOut = (event: PointerEvent) => {
        if (
            current !== null &&
            !(
                event.relatedTarget instanceof Node &&
                current.contains(event.relatedTarget)
            ) &&
            findTarget(event.relatedTarget) !== current
        ) {
            hide();
        }
    };
    const handleFocusIn = (event: FocusEvent) =>
        requestFocus(findFocusTarget(event.target));
    const handleFocusOut = (event: FocusEvent) => {
        if (
            !(event.relatedTarget instanceof Node) ||
            !root.contains(event.relatedTarget)
        ) {
            hide();
        }
    };

    root.addEventListener("pointerover", handlePointerOver);
    root.addEventListener("pointerout", handlePointerOut);
    root.addEventListener("focusin", handleFocusIn);
    root.addEventListener("focusout", handleFocusOut);
    return {
        hide,
        stop: () => {
            clearTimeout(openTimer);
            textObserver?.disconnect();
            root.removeEventListener("pointerover", handlePointerOver);
            root.removeEventListener("pointerout", handlePointerOut);
            root.removeEventListener("focusin", handleFocusIn);
            root.removeEventListener("focusout", handleFocusOut);
        },
    };
};

interface CellTooltipHostProps {
    /** The table root; listeners attach here. */
    root: HTMLElement | null;
    children: ReactNode;
}

/**
 * One truncation tooltip for every cell in a table, instead of one per cell.
 * `CellText` marks cut-off-able text with `data-cell-tooltip`; the host opens
 * on hover (after the tooltip delay) or keyboard focus of the active cell,
 * only while that text is actually cut off, and anchors to it. Elements that
 * also set `data-cell-tooltip-always` (a disabled row checkbox's reason) show
 * it whether or not anything is cut off. Without a
 * host, `CellText` falls back to its own tooltip.
 */
export const CellTooltipHost = ({
    root,
    children,
}: Readonly<CellTooltipHostProps>) => {
    const [target, setTarget] = useState<CellTooltipTarget | null>(null);
    const hideRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        if (root === null) {
            return undefined;
        }
        const listener = listenForTargets({ root, onTargetChange: setTarget });
        hideRef.current = listener.hide;
        return () => {
            listener.stop();
            setTarget(null);
        };
    }, [root]);

    return (
        <CellTooltipHostContext.Provider value>
            {children}
            <Tooltip
                anchor={target?.element ?? null}
                open={target !== null}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        hideRef.current();
                    }
                }}
                content={target?.text}
            />
        </CellTooltipHostContext.Provider>
    );
};
